import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoIcon from "@mui/icons-material/Info";
import NotInterestedRoundedIcon from "@mui/icons-material/NotInterestedRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { cnl } from "csi/typings";
import { MultiSelectProps } from "designSystem/components/select/MultiSelect";
import { Button, FlexColumn, FlexRow, GridColumn, ListItem, Message, MultiSelect, Text } from "designSystem/index";
import { blue, grey } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { ExpandableList } from "src/Sourcing/components/ExpandableList";
import FlexSideDrawer from "../../../designSystem/components/flex/FlexSideDrawer";
import ActionCard from "./ActionCard";
import {
	ColorSourceStatus,
	ProductSourceStore,
	useColorwayAllocationManageStore,
} from "./ColorwayAllocationManageStore";

export const ColorAllocationManagePage: FunctionComponent = () => {
	return (
		<FlexSideDrawer
			disabled
			drawerNode={<ColorAllocationDrawer />}
			open
			rightDrawer
			widthOpen={280}
		>
			<ColorAllocationForm />
		</FlexSideDrawer>
	);
};

const ColorAllocationForm: FunctionComponent = observer(() => {
	const store = useColorwayAllocationManageStore();
	const { productSourceStores, allocatedMessageRes } = store;
	const listItems = productSourceStores.map((psStore) => (
		<SourceColorAllocationField
			data-csi-automation={psStore.name}
			key={psStore.cnl}
			label={psStore.name}
			onValueChange={psStore.onChangeValue}
			options={psStore.colorAllocationOptions}
			ps_store={psStore}
			value={psStore.colorAllocationValues}
		/>
	));
	return (
		<GridColumn
			data-csi-automation="ColorAllocationForm"
			p={4}
			scroll
			spacing={4}
		>
			{allocatedMessageRes.length > 0 && (
				<Message
					data-csi-automation="ColorAllocationFormMessage"
					titleRes={allocatedMessageRes}
					variant="info"
				/>
			)}
			{listItems}
		</GridColumn>
	);
});

export interface SourceColorAllocationFieldProp extends MultiSelectProps<ComponentItem> {
	/* product source store */
	ps_store?: ProductSourceStore;
}

const SourceColorAllocationField: FunctionComponent<SourceColorAllocationFieldProp> = (props) => {
	const { ps_store } = props;

	const goToDialogAction =
		ps_store.colorAllocationOptions.find((allocation) => allocation.id?.includes("Empty_Colorway")) == null ? (
			<Button
				design="link"
				onClick={ps_store.onClickProductSourceGoToDialog}
				res="GoToDialog"
			/>
		) : (
			<></>
		);

	return (
		<MultiSelect
			disableClearable
			noChips
			placeHolderRes="Select"
			selectActions={[goToDialogAction]}
			{...props}
		/>
	);
};

const ColorAllocationDrawer: FunctionComponent = observer(() => {
	const store = useColorwayAllocationManageStore();
	const { areColorSourcesSelected, productSourceStores } = store;

	const supplierColorAllocationGroups = productSourceStores.map((ps) => (
		<ColorAllocationGroup
			key={ps.cnl}
			productSourceStore={ps}
		/>
	));
	return (
		<FlexColumn
			bgcolor={grey[200]}
			data-csi-automation="ColorAllocationOverview"
			gap={2}
			overflow="auto"
			p={2}
		>
			<Text
				res="AllocationOverview"
				variant="h5"
			/>
			<ColorAllocationInactivatedMessage />
			{areColorSourcesSelected ? supplierColorAllocationGroups : <Text res="DialogSelectionEmpty" />}
		</FlexColumn>
	);
});

const ColorAllocationInactivatedMessage: FunctionComponent = observer(() => {
	const store = useColorwayAllocationManageStore();
	const { inactivatedMessageText } = store;

	return (
		inactivatedMessageText.length > 0 && (
			<FlexRow gap={2}>
				<InfoIcon
					sx={{
						color: blue[500],
					}}
				/>
				<Text text={inactivatedMessageText} />
			</FlexRow>
		)
	);
});

interface ActionIconData {
	icon: React.ReactElement;
	iconAction: (id: cnl) => void;
	iconRes: string;
}

const ColorAllocationGroup: FunctionComponent<{ productSourceStore: ProductSourceStore }> = observer((props) => {
	const { productSourceStore } = props;

	const { name, colorSources, onActiveColorSource, onInactiveColorSource, onRemoveSelection } = productSourceStore;

	const getActionIcon = (status, type): ActionIconData => {
		switch (status) {
			case ColorSourceStatus.Added:
				return { icon: <CloseRoundedIcon />, iconAction: onRemoveSelection, iconRes: "Remove" };
			case ColorSourceStatus.Active:
				return {
					icon: <NotInterestedRoundedIcon />,
					iconAction: onInactiveColorSource,
					iconRes:
						type === "Colorway"
							? "ManageColorwayAllocation_Deactivate"
							: "ManageColorMaterialAllocation_Deactivate",
				};
			case ColorSourceStatus.Inactive:
				return {
					icon: <RefreshRoundedIcon />,
					iconAction: onActiveColorSource,
					iconRes:
						type === "Colorway"
							? "ManageColorwayAllocation_Activate"
							: "ManageColorMaterialAllocation_Activate",
				};
		}
	};

	const colorAllocationCards = colorSources.map((cs) => {
		const iconActionData = getActionIcon(cs.status, cs.node.$Type);
		return (
			<ListItem key={cs.id}>
				<ActionCard
					icon={iconActionData.icon}
					iconRes={iconActionData.iconRes}
					id={cs.id}
					inactive={!cs.active}
					labelColor={cs.statusColor}
					labelRes={cs.status}
					node={cs.node}
					onIconButtonClick={iconActionData.iconAction}
					text={cs.name}
				/>
			</ListItem>
		);
	});

	return (
		<>
			{colorSources.length > 0 && (
				<ExpandableList
					data-csi-automation={name}
					expanded
					items={colorAllocationCards}
					title={name}
				/>
			)}
		</>
	);
});

export default ColorAllocationManagePage;
